import React from "react";
import ReactGA from "react-ga4";
import { useParams } from "react-router-dom";
import { Grid, Typography } from "@mui/material";
import AttendeeList from "./components/AttendeeList";
// import Rounds from "./Rounds";
import MatchingControlPanel from "./components/MatchingControlPanel";
import EventTabs from "./components/EventTabs";
import RoundController from "./components/RoundController";
import { MatchContext } from "../../contexts/MatchContext";
import { EventContext } from "../../contexts/EventContext";

import request from "../../utilities/requests";
import { toast } from "../../utilities/SnackBarUtils";

const EventController = () => {
  const {
    pairingEvent,
    setPairingEvent,
    attendees,
    setAttendees,
    percentages,
    setPercentages,
    roundInfo,
    setRoundInfo,
    pairingData,
    setPairingData,
    setMissingRounds,
    setBackToBackBreaks,
    socket,
  } = React.useContext(EventContext);
  const { id: eventId } = useParams();
  // const [eventId, setEventId] = React.useState(null);
  const [filters, setFilters] = React.useState(null);
  const [showPairings, setShowPairings] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [warnings, setWarnings] = React.useState([]);
  // const [roundInfo, setRoundInfo] = React.useState(null);

  React.useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: "/eventcontroller",
      title: "EventController",
    });
  }, []);

  React.useEffect(() => {
    // Function to handle socket connection and event listener

    const handleSocketEvent = (data) => {
      console.log("Refreshing!");
      getAttendees(pairingEvent._id);
      getPairings(pairingEvent._id);
    };

    const handleConnectError = (err) => {
      // Silent self-heal: refresh token from localStorage and reconnect
      console.warn("[socket connect_error] attempting silent token refresh");
      try {
        const newToken = localStorage.getItem(
          `${process.env.REACT_APP_COGNITO_CLIENT_ID}.accessToken`
        );
        if (newToken) {
          socket.auth = { ...(socket.auth || {}), token: newToken };
          if (socket.connected) {
            socket.disconnect().connect();
          } else {
            socket.connect();
          }
        } else {
          // No token available; optional: keep a single console for debugging
          console.error(
            "[socket] no access token in localStorage; cannot refresh auth"
          );
        }
      } catch (e) {
        console.error("[socket] token refresh failed", e);
      }
    };

    const handleConnect = () => {
      // Clear any socket warning once we’re connected
      setWarnings((prev) => prev.filter((w) => w.type !== "socket"));
    };

    const handleAuthRotate = (data) => {
      localStorage.setItem(
        `${process.env.REACT_APP_COGNITO_CLIENT_ID}.accessToken`,
        data.accessToken
      );
      socket.auth.token = data.accessToken;
      if (socket.connected) {
        socket.disconnect().connect();
      }
    };

    const connectSocket = () => {
      if (!pairingEvent) return;

      // Always attach listeners (idempotent cleanup in return below)
      socket.on("connect_error", handleConnectError);
      socket.on("connect", handleConnect);
      socket.on("auth:rotate", handleAuthRotate);

      // Listen for this pairing event’s broadcast channel
      socket.on(pairingEvent._id, handleSocketEvent);

      if (!socket.connected) {
        console.log("Connecting to socket...");
        socket.connect();
      }
    };

    // Establish socket connection
    connectSocket();

    // Cleanup function to remove event listeners and disconnect socket
    return () => {
      if (pairingEvent) {
        console.log("Cleaning up socket...");
        socket.off(pairingEvent._id, handleSocketEvent);
      }
      socket.off("connect_error", handleConnectError);
      socket.off("connect", handleConnect);
      socket.disconnect();
    };
  }, [pairingEvent, socket]); //

  React.useEffect(() => {
    const getEventQuestionGroup = async () => {
      const result = await request(
        `questiongroups/eventquestiongroup?eventId=${eventId}`
      );
      if (result.success && !result?.eventQuestionGroup) {
        const warning = {
          type: "questionGroup",
          message:
            "There is no question group assigned to this event. Go to the settings tab below to assign one.",
        };

        setWarnings((prev) => [...prev, warning]);
      }
    };
    if (eventId) {
      getEventQuestionGroup();
    }
  }, [eventId]);

  // React.useEffect(() => {
  //   if (location.state?.pairingEvent) {
  //     setEventId(location.state?.pairingEvent?.event?._id);
  //   } else if (location.state?.event) {
  //     setEventId(location.state.event._id);
  //   }
  // }, [location]);

  React.useEffect(() => {
    if (eventId) {
      getData();
    }
  }, [eventId]);

  React.useEffect(() => {
    if (!roundInfo && pairingEvent) {
      setRoundInfo({
        roundStart: null,
        roundDuration: pairingEvent.roundDuration,
        paused: false,
      });
    }
  }, [pairingEvent]);

  const getData = async () => {
    try {
      let pairingEventId = await getEvent();
      // await getEventAttendees(eventId);
      await getAttendees(pairingEventId);
      await getPairings(pairingEventId);
    } catch (e) {
      console.error(`Error getting data: ${e.message}`);
    }
  };

  const getEvent = async () => {
    const result = await request(`pairingevent?eventId=${eventId}`);
    if (!result.success) {
      return;
    }
    setPairingEvent(result.pairingEvents);
    if (!result.pairingEvents?.pairingEventType) {
      const warning = {
        type: "pairingEventType",
        message:
          "No Event Type set. Please assign one in the Settings tab below.",
      };
      setWarnings((prev) => [...prev, warning]);
    }
    return result.pairingEvents._id;
  };

  const getAttendees = async (pairingEventId) => {
    const result = await request(
      `pairingevent/attendees?pairingEventId=${pairingEventId}`
    );
    if (result.success) {
      setAttendees(result.attendees);
      setPercentages(result.percentages);
    }
  };

  const getPairings = async (pairingEventId) => {
    const result = await request(
      `pairingevent/pairings?pairingEventId=${pairingEventId}`
    );
    if (!result.success) {
      return;
    }
    if (result.data) {
      // setData(result.data);
      setPairingData(result.data);
      if (result?.missingRounds) {
        setMissingRounds(result.missingRounds);
      }
      if (result?.backToBackBreaks) {
        setBackToBackBreaks(result.backToBackBreaks);
      }
    }
    console.log("attendees set");
  };

  React.useEffect(() => {
    if (!filters && eventId) {
      getFilters();
    }
  }, [eventId]);

  const getFilters = async () => {
    let result = await request(
      `questiongroups/eventfilters?eventId=${eventId}`
    );
    if (result.success) setFilters(result.filters);
  };

  const createMatches = async (update = false, maximizeDates, prevMatches) => {
    console.log("creating Matches");

    let checkedIn = attendees.filter((a) => a.checkedIn === true);
    console.log(`There are ${checkedIn.length} attendees checked in`);
    let attendeeList = checkedIn.map((a) => a.user);
    if (attendeeList.length === 0) {
      toast.error(
        "No attendees have checked in yet. How do you expect me to make any matches???"
      );
      return;
    } else if (attendeeList.length / attendees.length < 0.5) {
      toast.warning(
        "Less than half of the attendees have checked in. Matches probably won't be great. Proceeding anyway"
      );
    }

    let body = {
      pairingEventId: pairingEvent._id,
      filters,
      maximizeDates,
      prevMatches,
    };

    if (update) {
      body.update = true;
    }

    let result = await request("pairingevent/creatematches", "post", body);
    if (!result.success) {
      return;
    }
    setShowPairings(true);
    // setData(result.data);
    setPairingData(result.data);
    //set number of tables to the suggested number of tables based on the resulting matches
    // pairingEvent.numberOfTables = result.data.suggestedTables;
    //update number of rounds based on resulting matches
    // if (result?.data?.rounds) {
    //   let roundCount = 0;
    //   for (var r of result.data.rounds) {
    //     if (r.length > 0) {
    //       roundCount++;
    //     }
    //   }
    //   pairingEvent.numberOfRounds = roundCount;
    // }

    setPairingEvent({
      ...pairingEvent,
      numberOfTables: result.data.suggestedTables,
      numberOfRounds: result.data.numberOfRounds,
    });

    if (result?.data?.missingRounds) {
      setMissingRounds(result.data.missingRounds);
    }
    if (result?.data?.backToBackBreaks) {
      setBackToBackBreaks(result.data.backToBackBreaks);
    }

    return;
  };

  return (
    <MatchContext.Provider
      value={{
        createMatches,
        filters,
        setFilters,
        warnings,
        setWarnings,
        getData,
      }}
    >
      <Grid
        container
        direction="row"
        spacing={3}
        style={{ padding: 20, backgroundColor: "#f4f1f1" }}
      >
        {warnings.map((warning) => {
          return (
            <Grid
              item
              xs={12}
              style={{
                padding: 10,
                marginLeft: 20,
                marginTop: 20,
                backgroundColor: "pink",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography style={{ color: "#a60000", padding: 0 }}>
                {warning?.message}
              </Typography>
            </Grid>
          );
        })}

        <Grid xs={12} item>
          <MatchingControlPanel
            attendeeCount={attendees.length}
            percentages={percentages}
          />
        </Grid>
        {pairingEvent?.event?.status === "active" ? (
          <Grid item xs={12}>
            <RoundController />
          </Grid>
        ) : (
          <></>
        )}
        <Grid item xs={12}>
          <EventTabs showPairings={showPairings} data={pairingData} />
        </Grid>
      </Grid>
    </MatchContext.Provider>
  );
};

export default EventController;
